import DocElement from './DocElement';
import ImageElement from './ImageElement';
import AddDeleteDocElementCmd from '../commands/AddDeleteDocElementCmd';
import CommandGroupCmd from '../commands/CommandGroupCmd';
import SetValueCmd from '../commands/SetValueCmd';
import Band from '../container/Band';
import * as utils from '../utils';

/**
 * Table image doc element. An image element inside a table cell.
 *
 * This is the cell variant of ImageElement, mirroring TableTextElement being the cell
 * variant of TextElement. Image loading / preview / naming is inherited from ImageElement;
 * the table-cell machinery (colspan, width negotiation, <td> DOM, insert/delete column) is
 * the same content-agnostic logic used by TableTextElement.
 * @class
 */
export default class TableImageElement extends ImageElement {
    constructor(id, initialData, rb) {
        super(id, initialData, rb);
        // 'image' | 'text' - identifies the cell content type (selector in the property panel)
        this.cellType = initialData.cellType || 'image';
        // cell mechanics (mirrors TableTextElement)
        this.colspan = initialData.colspan || '';
        this.colspanVal = 1;
        this.columnIndex = initialData.columnIndex;
        this.parentId = initialData.parentId;
        this.tableId = initialData.tableId;
        this.displayWidth = this.widthVal;
        this.lastTouchStartTime = 0;
        this.updateColspanVal();
    }

    setInitialData(initialData) {
        this.growWeight = 0;
        super.setInitialData(initialData);
    }

    registerEventHandlers() {
        this.el.addEventListener('dblclick', (event) => {
            if (!this.rb.isSelectedObject(this.id)) {
                if (this.rb.isSelectedObject(this.tableId)) {
                    this.rb.selectObject(this.id, !event.shiftKey);
                    event.stopPropagation();
                }
            }
        });
        this.el.addEventListener('mousedown', (event) => {
            if (!this.rb.isSelectedObject(this.id)) {
                if (this.rb.isTableElementSelected(this.tableId)) {
                    this.rb.selectObject(this.id, !event.shiftKey);
                    event.stopPropagation();
                }
            } else {
                if (event.shiftKey) {
                    this.rb.deselectObject(this.id);
                }
                event.stopPropagation();
            }
        });
        this.el.addEventListener('touchstart', (event) => {
            if (!this.rb.isSelectedObject(this.id)) {
                let timeSinceLastTouch = new Date().getTime() - this.lastTouchStartTime;
                if (timeSinceLastTouch < 1000) {
                    if (this.rb.isSelectedObject(this.tableId)) {
                        this.rb.selectObject(this.id, true);
                        event.stopPropagation();
                    }
                } else {
                    if (this.rb.isTableElementSelected(this.tableId)) {
                        this.rb.selectObject(this.id, true);
                        event.stopPropagation();
                    }
                }
            }
            this.lastTouchStartTime = new Date().getTime();
        });
    }

    getContainerId() {
        let table = this.getTable();
        if (table !== null) {
            return table.getContainerId();
        }
        return null;
    }

    getValue(field) {
        if (field === 'xReadOnly') {
            return this.getOffsetX();
        }
        return super.getValue(field);
    }

    setValue(field, value) {
        if (field === 'cellType' && value !== this.cellType) {
            this.changeCellType(value);
            return;
        }
        super.setValue(field, value);

        if (field === 'width') {
            let table = this.getTable();
            if (table !== null) {
                table.updateColumnWidth(this.columnIndex, value);
                table.updateColumnDisplay();
            }
        } else if (field === 'height') {
            this.updateDisplayInternalNotify(0, 0, this.displayWidth, this.heightVal, false);
        } else if (field === 'colspan') {
            this.updateColspanVal();
            let tableObj = this.rb.getDataObject(this.tableId);
            if (tableObj !== null) {
                tableObj.updateColumnDisplay();
            }
        }
    }

    /**
     * Switch this cell between text and image content by rebuilding the table so the
     * correct cell class is instantiated in TableBandElement.createColumns.
     * Mirrors the delete/restore command-group pattern used for insert/delete column.
     * @param {String} newCellType - 'text' or 'image'.
     */
    changeCellType(newCellType) {
        let table = this.getTable();
        if (table === null) {
            this.cellType = newCellType;
            return;
        }
        let cmdGroup = new CommandGroupCmd('Change cell type');
        // snapshot before the change (old cellType still set) for undo
        let cmd = new AddDeleteDocElementCmd(false, table.getPanelItem().getPanelName(),
            table.toJS(), table.getId(), table.getContainerId(), -1, this.rb);
        cmdGroup.addCommand(cmd);
        // apply the new type on this cell, then snapshot the new state
        this.cellType = newCellType;
        cmd = new AddDeleteDocElementCmd(true, table.getPanelItem().getPanelName(),
            table.toJS(), table.getId(), table.getContainerId(), -1, this.rb);
        cmdGroup.addCommand(cmd);
        this.rb.executeCommand(cmdGroup);
        this.rb.selectObject(this.id, true);
    }

    getUpdateValue(field, value) {
        if (field === 'width') {
            let updateValue = utils.convertInputToNumber(value);
            if (this.colspanVal > 1) {
                let tableBandObj = this.rb.getDataObject(this.parentId);
                if (tableBandObj !== null) {
                    let nextCellIndex = this.getNextCellIndex();
                    let cellWidths = tableBandObj.getSingleCellWidths();
                    if (nextCellIndex > cellWidths.length) {
                        nextCellIndex = cellWidths.length;
                    }
                    for (let i = this.columnIndex + 1; i < nextCellIndex; i++) {
                        updateValue += cellWidths[i];
                    }
                }
            }
            if (value === '' && updateValue === 0) {
                return '';
            }
            return '' + updateValue;
        }
        return value;
    }

    setWidth(width) {
        this.width = width;
        this.widthVal = utils.convertInputToNumber(width);
    }

    getDisplayWidth() {
        return this.displayWidth;
    }

    setDisplayWidth(width) {
        this.displayWidth = width;
    }

    getDisplayWidthSplit(displayWidth) {
        if (this.colspanVal === 1) {
            return [displayWidth];
        }
        let minWidth = 20;
        let tableBandObj = this.rb.getDataObject(this.parentId);
        if (tableBandObj !== null) {
            let nextCellIndex = this.getNextCellIndex();
            let cellWidths = tableBandObj.getSingleCellWidths();
            if (nextCellIndex > cellWidths.length) {
                nextCellIndex = cellWidths.length;
            }
            let colCount = nextCellIndex - this.columnIndex;
            let currentTotal = 0;
            for (let i = this.columnIndex; i < nextCellIndex; i++) {
                currentTotal += cellWidths[i];
            }
            let totalMin = minWidth * colCount;
            if (displayWidth < totalMin) {
                displayWidth = totalMin;
            }
            let rv = [];
            if (currentTotal > 0) {
                let allocated = 0;
                for (let i = this.columnIndex; i < nextCellIndex - 1; i++) {
                    let w = Math.max(minWidth, Math.round(cellWidths[i] / currentTotal * displayWidth));
                    rv.push(w);
                    allocated += w;
                }
                rv.push(Math.max(minWidth, displayWidth - allocated));
            } else {
                let evenWidth = Math.max(minWidth, Math.floor(displayWidth / colCount));
                for (let i = 0; i < colCount - 1; i++) {
                    rv.push(evenWidth);
                }
                rv.push(Math.max(minWidth, displayWidth - evenWidth * (colCount - 1)));
            }
            return rv;
        }
        return [displayWidth];
    }

    updateColspanVal() {
        this.colspanVal = utils.convertInputToNumber(this.colspan);
        if (this.colspanVal <= 0) {
            this.colspanVal = 1;
        }
        if (this.el !== null) {
            this.el.setAttribute('colspan', this.colspanVal);
        }
    }

    /**
     * Returns all data fields of this object. The fields are used when serializing the object.
     * @returns {String[]}
     */
    getFields() {
        let fields = this.getProperties();
        // remove 'xReadOnly' field and add 'id'
        fields.splice(0, 1, 'id');
        return fields;
    }

    /**
     * Returns all fields of this object that can be modified in the properties panel.
     * @returns {String[]}
     */
    getProperties() {
        let fields = [
            'xReadOnly', 'width', 'cellType', 'source', 'image', 'imageFilename', 'colspan',
            'styleId', 'horizontalAlignment', 'verticalAlignment', 'backgroundColor', 'link'
        ];
        let tableBandObj = this.rb.getDataObject(this.parentId);
        if (tableBandObj !== null && tableBandObj.getValue('bandType') === Band.bandType.header) {
            fields.push('printIf');
            fields.push('growWeight');
        }
        return fields;
    }

    getElementType() {
        return DocElement.type.tableImage;
    }

    updateDisplay() {
        this.updateDisplayInternal(this.xVal, this.yVal, this.displayWidth, this.heightVal);
    }

    updateDisplayInternal(x, y, width, height) {
        this.updateDisplayInternalNotify(x, y, width, height, true);
    }

    updateDisplayInternalNotify(x, y, width, height, notifyTableElement) {
        if (this.el !== null) {
            // set td width to width - 1 because border consumes 1 pixel
            this.el.style.width = this.rb.toPixel(width - 1);
        }
        // fit the image within the cell box preserving aspect ratio (preview)
        let imgWidth = 0;
        let imgHeight = 0;
        if (this.imageRatio !== 0) {
            imgWidth = (this.imageWidth < width) ? this.imageWidth : width;
            imgHeight = (this.imageHeight < height) ? this.imageHeight : height;
            if (imgWidth !== this.imageWidth || imgHeight !== this.imageHeight) {
                let scaledWidth = Math.floor(imgHeight * this.imageRatio);
                if (scaledWidth < width) {
                    imgWidth = scaledWidth;
                } else {
                    imgHeight = Math.floor(imgWidth / this.imageRatio);
                }
            }
        }
        if (this.elImg !== null) {
            this.elImg.style.width = this.rb.toPixel(imgWidth);
            this.elImg.style.height = this.rb.toPixel(imgHeight);
        }

        if (notifyTableElement) {
            let tableObj = this.rb.getDataObject(this.tableId);
            if (tableObj !== null) {
                let tableBandObj = this.rb.getDataObject(this.parentId);
                let newTableWidth = width;
                let cellWidths = tableBandObj.getSingleCellWidths();
                for (let i = 0; i < cellWidths.length; i++) {
                    if (i < this.columnIndex || i >= (this.columnIndex + this.colspanVal)) {
                        newTableWidth += cellWidths[i];
                    }
                }
                let widths = this.getDisplayWidthSplit(width);
                for (let i = 0; i < widths.length; i++) {
                    tableObj.notifyColumnWidthResized(
                        tableBandObj, this.columnIndex + i, widths[i], newTableWidth);
                }
            }
        }
    }

    getSizers() {
        return ['E'];
    }

    hasBorderSettings() {
        return false;
    }

    isAreaSelectionAllowed() {
        return false;
    }

    isDraggingAllowed() {
        return false;
    }

    isDroppingAllowed() {
        return false;
    }

    getMinWidth() {
        return 20 * this.colspanVal;
    }

    getMaxWidth() {
        let tableObj = this.rb.getDataObject(this.tableId);
        let tableBandObj = this.rb.getDataObject(this.parentId);
        if (tableObj !== null && tableBandObj !== null) {
            let contentWidth = this.rb.getDocumentProperties().getContentSize().width;
            let widths = tableBandObj.getSingleCellWidths();
            let widthOther = 0;
            for (let i = 0; i < widths.length; i++) {
                if (i < this.columnIndex || i >= (this.columnIndex + this.colspanVal)) {
                    widthOther += widths[i];
                }
            }
            return contentWidth - widthOther - tableObj.xVal;
        }
        return 0;
    }

    getOffsetX() {
        let tableBandObj = this.rb.getDataObject(this.parentId);
        if (tableBandObj !== null) {
            let widths = tableBandObj.getSingleCellWidths();
            let offsetX = 0;
            for (let i = 0; i < this.columnIndex; i++) {
                offsetX += widths[i];
            }
            return offsetX;
        }
        return 0;
    }

    getCellIndex() {
        return this.columnIndex;
    }

    getNextCellIndex() {
        return this.columnIndex + this.colspanVal;
    }

    createElement() {
        this.el = utils.createElement(
            'td', { id: `rbro_el${this.id}`, class: 'rbroTableTextElement rbroTableImageElement' });
        this.elContent = utils.createElement(
            'div', { id: `rbro_el_content${this.id}`, class: 'rbroContentContainerHelper' });
        this.elImg = utils.createElement('img', { src: '' });
        this.elImg.addEventListener('load', (event) => {
            // get image width and height in load event, because width/height are not
            // directly available in some browsers after setting src
            this.imageWidth = this.elImg.naturalWidth;
            this.imageHeight = this.elImg.naturalHeight;
            if (this.imageHeight !== 0) {
                this.imageRatio = this.imageWidth / this.imageHeight;
            } else {
                this.imageRatio = 0;
            }
            this.updateDisplay();
        });
        this.elContent.append(this.elImg);
        this.el.append(this.elContent);
        if (this.colspanVal > 1) {
            this.el.setAttribute('colspan', this.colspanVal);
        }
        document.getElementById(`rbro_el_table_band${this.parentId}`).append(this.el);
        this.registerEventHandlers();
    }

    getParent() {
        return this.rb.getDataObject(this.parentId);
    }

    getTable() {
        return this.rb.getDataObject(this.tableId);
    }

    addCommandsForChangedWidth(newWidth, disableSelect, cmdGroup) {
        let widths = this.getDisplayWidthSplit(newWidth);
        let tableBand = this.getParent();
        if (tableBand !== null) {
            for (let i = widths.length - 1; i >= 0; i--) {
                let cmd = new SetValueCmd(
                    tableBand.getColumn(this.columnIndex + i).getId(),
                    'width', '' + widths[i], SetValueCmd.type.text, this.rb);
                if (disableSelect || i > 0) {
                    cmd.disableSelect();
                }
                cmdGroup.addCommand(cmd);
            }
        }
    }

    insertColumn(left) {
        let tableBand = this.getParent();
        let table = this.getTable();
        if (tableBand !== null && table !== null) {
            let colIndex = tableBand.getColumnIndex(this);
            if (colIndex !== -1) {
                let cmdGroup = new CommandGroupCmd('Insert column');
                let cmd = new AddDeleteDocElementCmd(false, table.getPanelItem().getPanelName(),
                    table.toJS(), table.getId(), table.getContainerId(), -1, this.rb);
                cmdGroup.addCommand(cmd);

                let columns = utils.convertInputToNumber(table.getValue('columns')) + 1;
                table.setValue('columns', columns);

                table.getValue('headerData').createColumns(columns, true, colIndex, left);
                for (let i = 0; i < table.getValue('contentDataRows').length; i++) {
                    table.getValue('contentDataRows')[i].createColumns(columns, true, colIndex, left);
                }
                table.getValue('footerData').createColumns(columns, true, colIndex, left);

                cmd = new AddDeleteDocElementCmd(true, table.getPanelItem().getPanelName(),
                    table.toJS(), table.getId(), table.getContainerId(), -1, this.rb);
                cmdGroup.addCommand(cmd);

                this.rb.executeCommand(cmdGroup);
                this.rb.selectObject(
                    this.getParent().getValue('columnData')[left ? colIndex : (colIndex + 1)].getId(), true);
            }
        }
    }

    deleteColumn() {
        let tableBand = this.getParent();
        let table = this.getTable();
        if (tableBand !== null && table !== null) {
            let colIndex = tableBand.getColumnIndex(this);
            if (colIndex !== -1) {
                let cmdGroup = new CommandGroupCmd('Delete column');
                let cmd = new AddDeleteDocElementCmd(false, table.getPanelItem().getPanelName(),
                    table.toJS(), table.getId(), table.getContainerId(), -1, this.rb);
                cmdGroup.addCommand(cmd);

                let columns = utils.convertInputToNumber(table.getValue('columns')) - 1;
                table.setValue('columns', columns);

                let tableWidth = table.getValue('widthVal');
                table.setValue('width', tableWidth - this.widthVal);

                table.getValue('headerData').deleteColumn(colIndex);
                for (let i = 0; i < table.getValue('contentDataRows').length; i++) {
                    table.getValue('contentDataRows')[i].deleteColumn(colIndex);
                }
                table.getValue('footerData').deleteColumn(colIndex);

                cmd = new AddDeleteDocElementCmd(true, table.getPanelItem().getPanelName(),
                    table.toJS(), table.getId(), table.getContainerId(), -1, this.rb);
                cmdGroup.addCommand(cmd);

                this.rb.executeCommand(cmdGroup);
            }
        }
    }

    toJS() {
        let rv = super.toJS();
        rv['growWeight'] = utils.convertInputToNumber(rv['growWeight']);
        return rv;
    }

    /**
     * Returns class name.
     * This can be useful for introspection when the class names are mangled
     * due to the webpack uglification process.
     * @returns {string}
     */
    getClassName() {
        return 'TableImageElement';
    }
}
